import React, { memo, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { message } from 'antd';
import useGlobalAntStyles from '../../../../hooks/use-global-ant-styles';
import { history } from '../../../../services';
import { isEmailValid } from '../../../../utils/email-validator';
import LoadingPage from '../../../common/loading-page';
import { checkAuthentication } from '../../helpers';
import OAuth from '../../o-auth';
import {
  AuthContentContainer,
  AuthTextField,
  Form,
  NavigationContainer,
  NavigationDividerContainer,
  NavigationLink,
  PageWrapper,
  SubmitButton,
  Title,
} from '../../StyledComponents';
import { logIn } from '../api';
import { getApiBaseUrl, QUIZ_PASSED_KEY_NAME } from '../../../../../common/constants/constants';
import { TurnstileCaptcha } from '../../StyledComponents/Captcha';
import { REGISTER_CONFIG } from '../../../../services/http/config';
import { handleCaptchaFrameError, requestNewCaptchaToken, requestNewHCaptchaToken } from '../../captcha.utils';
import HCaptcha from '../../StyledComponents/hcaptcha';

interface ICaptchaMessage {
  type: 'captcha-result';
  message: {
    captchaToken: string;
  };
}

interface IMessageShowCaptcha {
  type: 'show-captcha';
}

interface IHCaptchaMessageShowChallenge {
  type: 'hcaptcha-challenge-opened';
}

interface IHCaptchaMessageResult {
  type: 'hcaptcha-success';
  message: {
    captchaToken: string;
  };
}

type IMessage = ICaptchaMessage|IMessageShowCaptcha|IHCaptchaMessageShowChallenge|IHCaptchaMessageResult;
type TErrorInputs = 'email'|'password';
type HCaptchaContainerSize = { width: number; height: number };

const isElectron = !!window.require;
const { captchaUrl, hCaptchaUrl } = REGISTER_CONFIG;
const hCaptchaContainerSizeDefault: HCaptchaContainerSize = { width: 318, height: 82 };
const hCaptchaContainerSizeChallengeOpened: HCaptchaContainerSize = { width: 350, height: 370 };

const SignIn = () => {
  const [isUsingHCaptcha, setIsUsingHCaptcha] = useState<boolean>(getApiBaseUrl() === 'https://api.gologin.co');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNeedToShowCaptcha, setIsNeedToShowCaptcha] = useState(false);
  const [isCaptchaTokenNeed, setIsCaptchaTokenNeed] = useState(false);
  const [isFrameHidden, setIsFrameHidden] = useState(true);
  const [hCaptchaContainerSize, setHCaptchaContainerSize] = useState<HCaptchaContainerSize>(hCaptchaContainerSizeDefault);
  
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [errorInputs, setErrorInputs] = useState<TErrorInputs[]>([]);

  const { t: translation } = useTranslation();

  const emailFieldRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const passwordFieldRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const { areAntStylesLoading } = useGlobalAntStyles();

  useEffect(() => {
    setIsUsingHCaptcha(getApiBaseUrl() === 'https://api.gologin.co');
    setCaptchaToken('');
  }, [getApiBaseUrl()]);

  useEffect(() => {
    localStorage.setItem(QUIZ_PASSED_KEY_NAME, 'false');
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const isNeedToShowLoader = !isUsingHCaptcha && !captchaToken && isFrameHidden;
    setIsLoading(isNeedToShowLoader);
  }, [captchaToken, isFrameHidden]);

  useEffect(() => {
    if (!(isNeedToShowCaptcha && isCaptchaTokenNeed)) {
      return;
    }

    setIsFrameHidden(false);
  }, [isNeedToShowCaptcha, isCaptchaTokenNeed]);

  useEffect(() => {
    if (!isUsingHCaptcha) {
      return;
    }

    setHCaptchaContainerSize(hCaptchaContainerSizeDefault);
  }, [captchaToken]);

  const handleMessage = (ev: { data: IMessage }): void => {
    switch (ev.data.type) {
      case 'hcaptcha-success':
      case 'captcha-result': {
        const { message } = ev.data;
        const { captchaToken: token } = message;
        setCaptchaToken(token);

        break;
      }
      case 'show-captcha':
        setIsNeedToShowCaptcha(true);
  
        break;
      case 'hcaptcha-challenge-opened': {
        setHCaptchaContainerSize(hCaptchaContainerSizeChallengeOpened);

        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return (): void => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const onLoginSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    const email = emailFieldRef.current.value;
    const password = passwordFieldRef.current.value;

    const isEmailValidationSuccess = isEmailValid(email);
    if (!isEmailValidationSuccess) {
      emailFieldRef.current.focus();
      setErrorInputs(['email']);

      return;
    }

    if (!captchaToken && isCaptchaTokenNeed) {
      message.error(translation('auth.registration.captchaNotVerified'));

      if (!isUsingHCaptcha && isFrameHidden) {
        setIsLoading(true);
      }

      return;
    }

    try {
      setIsLoading(true);

      await logIn(email, password, captchaToken).catch((error) => {
        setCaptchaToken('');
        if (isUsingHCaptcha) {
          requestNewHCaptchaToken();
        } else {
          requestNewCaptchaToken();
        }

        const { body } = error || {};
        const { statusCode = 0, message = ''} = body || {};
        const isCaptchaError = typeof message === 'string' && statusCode === 403 && message.toLowerCase().includes('captcha');
        if (isCaptchaError) {
          setIsCaptchaTokenNeed(true);
        }

        throw error;
      });

      history.replace('/profileList');
    } finally {
      setIsLoading(false);
    }
  };

  if (areAntStylesLoading) {
    return <LoadingPage />;
  }

  return (
    <PageWrapper>
      <AuthContentContainer>
        <Title>
          {translation('auth.login.title')}
        </Title>

        <OAuth.Buttons isElectron={isElectron} />

        <Form onSubmit={onLoginSubmit}>
          <AuthTextField
            ref={emailFieldRef}
            status={errorInputs.includes('email') ? 'error' : 'default'}
            placeholder={translation('auth.login.form.emailInputPlaceholder')}
            type='email'
            required={true}
            autoFocus={true}
            onChange={() => setIsCaptchaTokenNeed(false)}
          />
          <AuthTextField
            ref={passwordFieldRef}
            status={errorInputs.includes('email') ? 'error' : 'default'}
            placeholder={translation('auth.login.form.passwordInputPlaceholder')}
            type='password'
            required={true}
          />

          <SubmitButton type='submit' loading={isLoading}>
            {translation('auth.login.form.submitBtnTxt')}
          </SubmitButton>
        </Form>
        {!isUsingHCaptcha ?
          <TurnstileCaptcha
            id='captcha-iframe'
            src={captchaUrl}
            onError={handleCaptchaFrameError}
            isFrameHidden={isFrameHidden}
          /> :
          null
        }
        {isUsingHCaptcha ?
          <HCaptcha
            width={hCaptchaContainerSize.width}
            height={hCaptchaContainerSize.height}
            src={hCaptchaUrl}
          /> :
          null
        }
        <NavigationContainer style={{ marginTop: isFrameHidden ? 32 : 27 }}>
          <NavigationLink to='/sign_up'>
            {translation('auth.login.links.signUp')}
          </NavigationLink>
          <NavigationDividerContainer>
            •
          </NavigationDividerContainer>
          <NavigationLink to='/forgot_password'>
            {translation('auth.login.links.forgotPassword')}
          </NavigationLink>
        </NavigationContainer>
      </AuthContentContainer>

      <Helmet>
        <title>
          {translation('auth.login.windowTitle')}
          {' '}
          - GoLogin
          {' '}
          {window.gologinAppVersion || ''}
        </title>
      </Helmet>
    </PageWrapper>
  );
};

export default memo(SignIn);
